import { createContext, useContext } from 'react';

import noop, { NoOp } from 'utils/noop';

import { DropZoneArea, ImageMods, NewImage } from '../types';
import { defaults } from '../constants';

export interface UploadAssetsContextState {
  activeDropZone: DropZoneArea;
  canEditBeforeUpload: boolean;
  clearPendingUploads: NoOp;
  editingImage: number | null;
  handleNewFiles: (files: File[], cb?: NoOp) => void;
  handleSaveEdits: NoOp;
  handleUploadPending: NoOp;
  maxBatchSize: number;
  maxFileSize: number;
  maxImgDimension: number;
  onEditFiles: (ids: number[]) => void;
  onModifyPendingAssets: (ids: string[], values: Record<string, any>) => void;
  pendingImages: NewImage[];
  pendingUploadCount: number;
  removeCompletedUploads: NoOp;
  removeSelectedUploads: (toRemove: string[]) => void;
  setActiveDropZone: (dropZone: DropZoneArea) => void;
  setEditingImage: (num: number) => void;
  setMaxBatchSize: (num: number) => void;
  setMaxFileSize: (num: number) => void;
  setMaxImgDimension: (num: number) => void;
  setPendingImageMod: (id: string, mods: ImageMods) => void;
  setSelectedAssets: (ids: string[]) => void;
  updatePendingImagesDestination: (
    ids: string[],
    destinationFolderId: number,
    destinationFolderName: string,
  ) => void;
  isUploading: boolean;
}

export const uploadAssetsContextDefaults: UploadAssetsContextState = {
  activeDropZone: DropZoneArea.None,
  canEditBeforeUpload: false,
  clearPendingUploads: noop,
  editingImage: null,
  handleNewFiles: noop,
  handleSaveEdits: noop,
  handleUploadPending: noop,
  maxBatchSize: defaults.maxBatchSize,
  maxFileSize: defaults.maxFileSize,
  maxImgDimension: defaults.maxImgDimension,
  onEditFiles: noop,
  onModifyPendingAssets: noop,
  pendingImages: [],
  pendingUploadCount: 0,
  removeCompletedUploads: noop,
  removeSelectedUploads: noop,
  setActiveDropZone: noop,
  setEditingImage: noop,
  setMaxBatchSize: noop,
  setMaxFileSize: noop,
  setMaxImgDimension: noop,
  setPendingImageMod: noop,
  setSelectedAssets: noop,
  updatePendingImagesDestination: noop,
  isUploading: false,
};

export const UploadAssetsContext = createContext<UploadAssetsContextState>(
  uploadAssetsContextDefaults,
);

export const useUploadAssetsContext = () => useContext(UploadAssetsContext);
